import { loadBillingScheduleFromPhases } from 'Cube/communication/external/billingSchedule.api.v1/adapters/billingSchedule.adapters.phases.in'
import { NEW_PHASE_PREFIX } from 'Cube/domain/cube.constants'
import { CubeEditorData } from 'Cube/domain/cube.domain.types'
import { splitSchedulePhasesAtDate } from '@sequencehq/utils'
import { AmendableSchedule } from './useAmendableSchedules'

const AMENDMENT_PHASE_NAME = 'Amendment'

/**
 * Turns the schedule being amended into the phases, discounts and minimums a
 * quote works with, splitting the phase the amendment date falls in so the
 * second half is the part the user edits.
 */
export const amendmentScheduleToCubeData = (args: {
  schedule: AmendableSchedule
  amendmentDate: string /** `yyyy-MM-dd` */
}): {
  amendmentPhaseId: string
  common: Pick<CubeEditorData['common'], 'startDate' | 'currency' | 'phaseIds'>
  phases: CubeEditorData['phases']
  discounts: CubeEditorData['discounts']
  minimums: CubeEditorData['minimums']
} => {
  const { phases, amendmentPhaseId } = splitSchedulePhasesAtDate({
    phases: args.schedule.phases,
    splitDate: args.amendmentDate,
    createPhaseId: () => `${NEW_PHASE_PREFIX}${crypto.randomUUID()}`
  })

  const loadedSchedule = loadBillingScheduleFromPhases(
    {
      billingSchedule: { ...args.schedule, phases },
      prices: args.schedule.prices
    },
    {
      creditGrantsEditingEnabled: false,
      contractReferenceEditingEnabled: false
    }
  )

  return {
    amendmentPhaseId,
    common: {
      startDate: loadedSchedule.common.startDate,
      currency: loadedSchedule.common.currency,
      phaseIds: loadedSchedule.common.phaseIds
    },
    phases: Object.values(loadedSchedule.phases).reduce(
      (acc, phase) => ({
        ...acc,
        [phase.id]: {
          ...phase,
          name:
            phase.id === amendmentPhaseId ? AMENDMENT_PHASE_NAME : phase.name,
          /**
           * A quote cannot persist a recurrence preference
           */
          recurrencePreference: 'CONTINUE_FROM_PREVIOUS_PHASE' as const
        }
      }),
      {} as CubeEditorData['phases']
    ),
    discounts: loadedSchedule.discounts,
    minimums: loadedSchedule.minimums
  }
}
